<template>
  <div id="app">
    <router-view/>
  </div>
</template>

<script>
export default {
  name: 'App'
}
</script>

<style lang="scss">
// ========== V3品牌高级配色体系 ==========
// 主色：松石墨绿 - 沉稳内敛，体现高端质感
$primary: #1A3A32;
$primary-dark: #0F2822;
$primary-light: #2E5A4B;
// 点缀色：暖金色 - 轻奢质感
$accent: #C8A961;
$accent-light: #D4B87A;
$accent-dark: #A88D4A;
// 背景
$bg-warm: #F8F6F1;
$bg-white: #FFFFFF;
$bg-light: #F0EDE6;
// 文字
$text-primary: #1A1A1A;
$text-secondary: #4A4A4A;
$text-muted: #8A8A8A;
// 边框
$border: #E8E4DE;
$border-light: #F0EDE6;

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Noto Serif SC', 'Microsoft YaHei', 'PingFang SC', serif;
  color: $text-primary;
  line-height: 1.8;
  -webkit-font-smoothing: antialiased;
  background: $bg-warm;
  font-size: 15px;
}

a {
  text-decoration: none;
  color: inherit;
  transition: color 0.3s ease;
}

// 滚动条
::-webkit-scrollbar { width: 6px; height: 6px; }
::-webkit-scrollbar-track { background: $bg-warm; }
::-webkit-scrollbar-thumb { background: $primary-light; border-radius: 3px; }
::-webkit-scrollbar-thumb:hover { background: $primary; }

// Element UI 主题覆盖
.el-button--primary {
  background-color: $primary;
  border-color: $primary;
  &:hover, &:focus {
    background-color: $primary-dark;
    border-color: $primary-dark;
  }
}

// 容器
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

// 页面标题栏（统一 - 全屏大图沉浸式）
.page-banner {
  height: 55vh;
  min-height: 380px;
  max-height: 600px;
  background: linear-gradient(135deg, $primary-dark 0%, $primary 40%, $primary-light 100%);
  text-align: center;
  color: #fff;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle at 30% 50%, rgba(200, 169, 97, 0.08) 0%, transparent 50%);
  }

  .banner-inner {
    position: relative;
    z-index: 1;
    padding: 0 20px;
  }

  .page-title {
    font-size: 48px;
    font-weight: 600;
    margin-bottom: 16px;
    letter-spacing: 6px;
    position: relative;
    text-shadow: 0 2px 12px rgba(0,0,0,0.15);
  }

  .page-subtitle {
    font-size: 18px;
    opacity: 0.85;
    letter-spacing: 3px;
    position: relative;
    font-weight: 300;
  }

  .page-line {
    width: 60px;
    height: 2px;
    background: $accent;
    margin: 20px auto 0;
    position: relative;
  }

  // 滚动引导箭头
  .scroll-hint {
    position: absolute;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    opacity: 0.5;
    animation: scrollBounce 2s ease-in-out infinite;

    span { font-size: 11px; letter-spacing: 2px; }
    .arrow { font-size: 16px; line-height: 1; }
  }
}

@keyframes scrollBounce {
  0%, 100% { transform: translateX(-50%) translateY(0); }
  50% { transform: translateX(-50%) translateY(6px); }
}

// 区块标题
.section-header {
  text-align: center;
  margin-bottom: 50px;
  
  &.left { text-align: left; }
}

.section-title {
  font-size: 32px;
  color: $text-primary;
  font-weight: 600;
  letter-spacing: 2px;
  margin-bottom: 12px;
}

.section-desc {
  font-size: 15px;
  color: $text-muted;
  letter-spacing: 1px;
}

.section-line {
  width: 50px;
  height: 2px;
  background: $accent;
  margin: 16px auto;
  
  &.left { margin-left: 0; }
}

// 卡片通用样式
.card {
  background: $bg-white;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.4s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);

  &:hover {
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
    transform: translateY(-4px);
  }
}

// 金色按钮
.btn-gold {
  display: inline-block;
  padding: 12px 36px;
  background: linear-gradient(135deg, $accent 0%, $accent-light 100%);
  color: #fff;
  border-radius: 4px;
  font-size: 15px;
  letter-spacing: 2px;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;

  &:hover {
    background: linear-gradient(135deg, $accent-dark 0%, $accent 100%);
    box-shadow: 0 4px 15px rgba(200, 169, 97, 0.3);
  }
}

// 墨绿按钮
.btn-primary {
  display: inline-block;
  padding: 12px 36px;
  background: $primary;
  color: #fff;
  border-radius: 4px;
  font-size: 15px;
  letter-spacing: 2px;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;

  &:hover {
    background: $primary-dark;
    box-shadow: 0 4px 15px rgba(26, 58, 50, 0.3);
  }
}

// 页面内容区
.page-content {
  padding: 60px 0 80px;
}

// 区块交替背景
.section-dark {
  background: $primary;
  color: #fff;
}

.section-warm {
  background: $bg-warm;
}

.section-white {
  background: $bg-white;
}

// 响应式
@media (max-width: 768px) {
  .page-banner {
    height: 40vh;
    min-height: 280px;
  }
  .page-banner .page-title {
    font-size: 28px;
    letter-spacing: 4px;
  }
  .page-banner .page-subtitle {
    font-size: 14px;
  }
  .page-banner .scroll-hint {
    bottom: 16px;
  }
}

</style>
